import { useCallback, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { drawolaT } from "../storage/i18n";
import { getDocMeta } from "../storage/multiFileStore";
import { formatMB } from "../storage/quota";

import { NetworkError, TokenExpiredError } from "./errors";

import type { useMultiFileStore } from "../storage/useMultiFileStore";
import type { CloudSyncApi } from "./sync";
import type {
  CloudFileMeta,
  CloudProviderId,
  CloudStorageProvider,
} from "./types";

type Args = {
  excalidrawAPI: ExcalidrawImperativeAPI;
  multiFile: ReturnType<typeof useMultiFileStore>;
  cloudSync: CloudSyncApi;
};

/**
 * The Cloud submenu's view of the sync engine: provider switcher, sign in and
 * out, the file picker, "save now" and "save as". Every write goes through
 * the engine, so the menu and the background sync agree on the binding.
 */
export function useCloudStorage({ excalidrawAPI, multiFile, cloudSync }: Args) {
  const { engine } = cloudSync;
  const [providerId, setProviderId] = useState<CloudProviderId>("googleDrive");
  const [conflictDocId, setConflictDocId] = useState<string | null>(null);
  const [conflictRemote, setConflictRemote] = useState<CloudFileMeta | null>(
    null,
  );

  const provider = engine.getProvider(providerId);
  // `cloudSync.version` changes on every engine event, which re-renders the
  // caller, so reading the auth state here is enough to stay current.
  const authState = engine.getAuthState(providerId);

  /** Show a one-line message on the canvas. */
  const toast = useCallback(
    (message: string) => {
      excalidrawAPI.setToast({ message });
    },
    [excalidrawAPI],
  );

  /** Turn a provider failure into a toast, or log what we cannot explain. */
  const handleProviderError = useCallback(
    (e: unknown): void => {
      if (e instanceof TokenExpiredError) {
        toast(drawolaT("cloud.tokenExpired"));
        return;
      }
      if (e instanceof NetworkError) {
        toast(drawolaT("cloud.networkError"));
        return;
      }
      // eslint-disable-next-line no-console
      console.error("[drawOla] cloud error", e);
    },
    [toast],
  );

  /** False and a toast when the browser says we are offline. */
  const ensureOnline = useCallback((): boolean => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      toast(drawolaT("cloud.networkOffline"));
      return false;
    }
    return true;
  }, [toast]);

  /** Interactive sign-in for the selected provider. */
  const signIn = useCallback(async () => {
    if (!ensureOnline()) {
      return;
    }
    await engine.signIn(providerId);
  }, [engine, providerId, ensureOnline]);

  /** Drop the session of the selected provider. */
  const signOut = useCallback(async () => {
    await provider?.signOut();
  }, [provider]);

  /** Every cloud file this build can see, for the picker. */
  const listFiles = useCallback(async (): Promise<CloudFileMeta[]> => {
    if (!provider || !ensureOnline()) {
      return [];
    }
    try {
      return await provider.listFiles();
    } catch (e) {
      handleProviderError(e);
      return [];
    }
  }, [provider, ensureOnline, handleProviderError]);

  /** Open a cloud file: reuse the local draw bound to it, or make one. */
  const openFile = useCallback(
    async (fileId: string) => {
      if (!ensureOnline()) {
        return;
      }
      try {
        const docId = await engine.openRemote(providerId, fileId);
        if (!docId) {
          return;
        }
        await engine.pullIfRemoteNewer(docId);
        await multiFile.switchTo(docId);
      } catch (e) {
        handleProviderError(e);
      }
    },
    [engine, providerId, multiFile, ensureOnline, handleProviderError],
  );

  /** Tell the user the draw is over what the provider takes in one upload. */
  const tooLargeToast = useCallback(
    (target: CloudStorageProvider | null = provider) => {
      toast(
        drawolaT("cloud.fileTooLarge", {
          limit: formatMB(target?.maxUploadBytes ?? 0),
        }),
      );
    },
    [provider, toast],
  );

  /** Always a new cloud file, bound to the active draw. */
  const saveAs = useCallback(
    async (name: string): Promise<CloudFileMeta | undefined> => {
      const docId = multiFile.activeDocId;
      if (!docId || !provider || !ensureOnline()) {
        return undefined;
      }
      const result = await engine.saveNew(docId, providerId, name);
      if (result.ok) {
        toast(drawolaT("cloud.savedTo", { provider: provider.displayName }));
        return result.meta;
      }
      if (result.reason === "tooLarge") {
        tooLargeToast();
      } else if (
        result.reason === "unavailable" &&
        engine.getAuthState(providerId).status !== "signed-in"
      ) {
        // "unavailable" while signed in means offline, which `ensureOnline`
        // has already explained. A lost session is the case with no other
        // sign of what went wrong.
        toast(drawolaT("cloud.tokenExpired"));
      }
      return undefined;
    },
    [
      engine,
      providerId,
      provider,
      multiFile.activeDocId,
      ensureOnline,
      toast,
      tooLargeToast,
    ],
  );

  /**
   * "Save now": push a bound draw at once (the engine detects a conflict and
   * this opens the modal), or create a cloud file for an unbound one.
   */
  const save = useCallback(
    async (name?: string) => {
      const docId = multiFile.activeDocId;
      if (!docId || !provider || !ensureOnline()) {
        return;
      }
      const before = await getDocMeta(docId);
      if (!before?.cloud) {
        await saveAs(name ?? before?.name ?? "Untitled");
        return;
      }
      // The dropdown picks the provider for a first save only. A draw that is
      // already bound goes back to the provider it is bound to, whatever the
      // menu shows.
      const bound = engine.getProvider(before.cloud.providerId);
      const boundName = engine.providerName(before.cloud.providerId);
      await multiFile.flushSave();
      await engine.push(docId);
      const after = await getDocMeta(docId);
      const error = after?.cloud?.syncError;
      if (error === "conflict" && after?.cloud) {
        setConflictDocId(docId);
        try {
          setConflictRemote(
            (await bound?.statFile(after.cloud.providerFileId)) ?? null,
          );
        } catch {
          setConflictRemote(null);
        }
        return;
      }
      if (error === "tooLarge") {
        tooLargeToast(bound);
        return;
      }
      if (!after) {
        return;
      }
      const status = cloudSync.statusOf(after);
      if (status.kind === "error" && status.reason === "auth") {
        toast(drawolaT("cloud.tokenExpired"));
        return;
      }
      if (status.kind === "synced") {
        toast(drawolaT("cloud.savedTo", { provider: boundName }));
        return;
      }
      if (status.kind === "pending") {
        // The engine kept the draw dirty and scheduled a retry, so the upload
        // did not land. Say so instead of leaving the click without an answer.
        toast(drawolaT("cloud.networkError"));
      }
    },
    [
      engine,
      provider,
      multiFile,
      ensureOnline,
      saveAs,
      cloudSync,
      toast,
      tooLargeToast,
    ],
  );

  /** Remove a cloud file from the picker. The local draw is not touched. */
  const deleteFile = useCallback(
    async (fileId: string) => {
      if (!provider || !ensureOnline()) {
        return;
      }
      try {
        await provider.deleteFile(fileId);
      } catch (e) {
        handleProviderError(e);
      }
    },
    [provider, ensureOnline, handleProviderError],
  );

  /** Rename a cloud file from the picker. */
  const renameFile = useCallback(
    async (fileId: string, name: string) => {
      if (!provider || !ensureOnline()) {
        return;
      }
      try {
        await provider.renameFile(fileId, name);
      } catch (e) {
        handleProviderError(e);
      }
    },
    [provider, ensureOnline, handleProviderError],
  );

  /** Close the conflict modal and forget which draw it was about. */
  const closeConflict = useCallback(() => {
    setConflictDocId(null);
    setConflictRemote(null);
  }, []);

  /** Conflict answer: keep the local copy. */
  const onOverwrite = useCallback(async () => {
    if (conflictDocId) {
      await engine.resolveConflict(conflictDocId, "overwrite");
    }
    closeConflict();
  }, [engine, conflictDocId, closeConflict]);

  /** Conflict answer: take the cloud copy. */
  const onLoadRemote = useCallback(async () => {
    if (conflictDocId) {
      await engine.resolveConflict(conflictDocId, "loadRemote");
    }
    closeConflict();
  }, [engine, conflictDocId, closeConflict]);

  return {
    providerId,
    setProviderId,
    displayName: provider?.displayName ?? "",
    authState,
    signIn,
    signOut,
    listFiles,
    openFile,
    save,
    saveAs,
    deleteFile,
    renameFile,
    conflictOpen: conflictDocId !== null,
    conflictRemote,
    onOverwrite,
    onLoadRemote,
    onCancel: closeConflict,
  };
}
